import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  KeyIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  LinkIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from 'components/icons';

import { userApi } from '../../services/api';

// =============================================================================
// API keys
// =============================================================================
//
// The one screen in the product that shows a secret exactly once. Everything
// here is arranged around that: the new key is impossible to miss, impossible
// to dismiss by accident, and the panel says plainly that closing it loses the
// key rather than letting someone discover that later.

interface Scope {
  id: string;
  description: string;
}

interface Connection {
  clientId: string;
  clientName: string;
  clientUri: string | null;
  scopes: string[];
  connectedAt: string;
  lastUsedAt: string | null;
}

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  permissions: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  dailyCreditCap: number | null;
  creditsSpentToday: number;
}

// The only scope that can spend money. Kept out of the default selection and
// given its own section below: every other permission is recoverable by
// revoking the key, and this one is not — credits spent are gone.
const SPEND_SCOPE = 'run:analysis';
const DEFAULT_DAILY_CAP = 25;

// Ordered shortest-first, and 90 days is the default. A key is a static secret
// that ends up in shell history, .env files and screenshots; "never expires" is
// the wrong default for the credential type most likely to leak. It stays
// available, last, for the cron job nobody wants to re-key every quarter.
const EXPIRY_CHOICES: { label: string; value: number | undefined }[] = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
  { label: 'Never', value: undefined },
];

const DEFAULT_EXPIRY_DAYS = 90;

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const ApiKeysPanel: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number | undefined>(DEFAULT_EXPIRY_DAYS);
  const [dailyCreditCap, setDailyCreditCap] = useState<number>(DEFAULT_DAILY_CAP);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [freshKey, setFreshKey] = useState<{ value: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  // Keys start collapsed. Signing an app in is the path almost everyone should
  // take; a key is for the case where there is no browser to sign in with.
  // Opened automatically when the account already has keys, so an existing one
  // is never hidden from the person who made it.
  const [showKeys, setShowKeys] = useState(false);

  const load = useCallback(async () => {
    try {
      // Scopes come from the server rather than a copy kept here. A hardcoded
      // list drifts the moment a scope is added, and the failure mode is
      // offering people a permission the server will reject.
      const [keyRes, scopeRes, connRes] = await Promise.all([
        userApi.listApiKeys(),
        userApi.getApiKeyScopes(),
        userApi.listConnections(),
      ]);
      const loadedKeys = keyRes.data.apiKeys || [];
      setKeys(loadedKeys);
      if (loadedKeys.length > 0) setShowKeys(true);
      setConnections(connRes.data.connections || []);
      const available: Scope[] = scopeRes.data.scopes || [];
      setScopes(available);
      setChosen((prev) =>
        prev.length ? prev : available.map((s) => s.id).filter((id) => id !== SPEND_SCOPE),
      );
    } catch {
      toast.error('Could not load your API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const res = await userApi.createApiKey({
        name: name.trim(),
        permissions: chosen,
        expiresInDays,
        // Only meaningful alongside the spending scope, and sending it
        // otherwise would store a cap on a key that can never reach it.
        dailyCreditCap: chosen.includes(SPEND_SCOPE) ? dailyCreditCap : undefined,
      });
      setFreshKey({ value: res.data.key, name: res.data.apiKey.name });
      setCopied(false);
      setName('');
      setExpiresInDays(DEFAULT_EXPIRY_DAYS);
      setDailyCreditCap(DEFAULT_DAILY_CAP);
      setChosen(scopes.map((sc) => sc.id).filter((id) => id !== SPEND_SCOPE));
      setShowForm(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not create the key.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key: ApiKey) => {
    // Typed confirmation rather than an OK button. Revoking is instant and
    // irreversible, and anything still using the key stops working the moment
    // it lands — that deserves more friction than a misplaced click.
    const answer = window.prompt(
      `Revoking "${key.name}" takes effect immediately. Anything using it will stop working and it cannot be restored.\n\nType the key's name to confirm:`,
    );
    if (answer === null) return;
    if (answer.trim() !== key.name) {
      toast.error('Name did not match — nothing was revoked.');
      return;
    }
    try {
      await userApi.revokeApiKey(key.id);
      toast.success(`Revoked "${key.name}".`);
      await load();
    } catch {
      toast.error('Could not revoke the key.');
    }
  };

  const disconnect = async (conn: Connection) => {
    if (
      !window.confirm(
        `Disconnect ${conn.clientName}? It stops working immediately and will have to sign in again to reconnect.`,
      )
    ) {
      return;
    }
    try {
      await userApi.revokeConnection(conn.clientId);
      toast.success(`Disconnected ${conn.clientName}.`);
      await load();
    } catch {
      toast.error('Could not disconnect that app.');
    }
  };

  const copyServerUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/mcp`);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast.error('Could not copy — select the address and copy it.');
    }
  };

  const copy = async () => {
    if (!freshKey) return;
    try {
      await navigator.clipboard.writeText(freshKey.value);
      setCopied(true);
    } catch {
      // Clipboard access is blocked in some browsers and over plain HTTP. The
      // key is on screen and selectable, so this is not a dead end — say so
      // instead of failing silently.
      toast.error('Could not copy automatically — select the key and copy it.');
    }
  };

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------------- */}
      {/* Connect an app — the path almost everyone should take              */}
      {/* ---------------------------------------------------------------- */}
      {/* There is no "Connect" button here on purpose: an OAuth flow is
          started by the client, not by us. What a person needs from this
          screen is the server address, so that is what it gives them. */}
      <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-5">
        <h4 className="font-semibold text-content">Connect an app</h4>
        <p className="mt-1 text-sm text-content-muted">
          Add this address as an MCP server in Claude, ChatGPT, Cursor or your editor. You'll be
          asked to sign in and approve what it may read — no key to copy, nothing to keep secret.
        </p>

        <div className="mt-4 flex items-stretch gap-2">
          <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-border bg-surface px-3 py-2.5 font-mono text-sm text-content">
            {window.location.origin}/api/mcp
          </code>
          <button
            onClick={copyServerUrl}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-md px-4 text-sm font-medium transition-colors ${
              copiedUrl ? 'bg-green-600 text-white' : 'bg-brand-600 text-white hover:bg-brand-700'
            }`}
          >
            {copiedUrl ? (
              <>
                <CheckIcon className="h-4 w-4" /> Copied
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-4 w-4" /> Copy
              </>
            )}
          </button>
        </div>

        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="w-56 flex-shrink-0 text-content-muted">ChatGPT</dt>
            <dd className="text-content">Settings → Connectors → Create. A standard connector gets search and fetch; Developer mode gets every tool.</dd>
          </div>
          <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="w-56 flex-shrink-0 text-content-muted">claude.ai, Claude Desktop</dt>
            <dd className="text-content">Settings → Connectors → Add custom</dd>
          </div>
          <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="w-56 flex-shrink-0 text-content-muted">Claude Code</dt>
            <dd className="font-mono text-xs text-content">
              claude mcp add --transport http clevor {window.location.origin}/api/mcp
            </dd>
          </div>
        </dl>

        <p className="mt-4 flex items-start gap-2 text-sm text-content-muted">
          <LockClosedIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-600" />
          <span>
            Read-only. An agent can search solicitations, check what you're eligible for and read
            your pipeline, proposals and past performance. It cannot change anything and cannot
            spend your AI credits.{' '}
            <Link to="/for-agents" className="font-medium underline">
              What's coming
            </Link>
            .
          </span>
        </p>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Connected apps                                                     */}
      {/* ---------------------------------------------------------------- */}
      {/* Shown above the keys because it is the list most people will have
          something in: signing ChatGPT or Claude in is a couple of clicks,
          where creating a key means knowing what one is. Rendered only when
          non-empty — an empty-state for a feature you may never use is noise. */}
      {connections.length > 0 && (
        <div>
          <h4 className="mb-1 font-semibold text-content">Connected apps</h4>
          <p className="mb-3 text-sm text-content-muted">
            Apps you signed in to. They use the same read-only tools as an API key.
          </p>
          <div className="overflow-hidden rounded-lg border border-border">
            {connections.map((conn, i) => (
              <div
                key={conn.clientId}
                className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4 ${
                  i > 0 ? 'border-t border-border' : ''
                }`}
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50">
                    <LinkIcon className="h-4 w-4 text-brand-600" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-content">{conn.clientName}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {conn.scopes.map((sc) => (
                        <span
                          key={sc}
                          className="rounded bg-surface-sunk px-1.5 py-0.5 font-mono text-[10px] text-content-muted"
                        >
                          {sc}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 text-xs text-content-muted sm:w-40">
                  <span className="text-content-subtle">Last used </span>
                  {conn.lastUsedAt ? formatDate(conn.lastUsedAt) : 'never'}
                </div>
                <button
                  onClick={() => disconnect(conn)}
                  className="flex flex-shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* API keys — the headless path                                       */}
      {/* ---------------------------------------------------------------- */}
      {/* Collapsed by default, and second on the page. A key is a static
          secret with no expiry pressure and no sign-in behind it: it ends up
          in shell history, .env files and screenshots, which is exactly how
          credentials leak. Signing an app in is better in every case except
          the one this exists for — a cron job or a server with no browser to
          sign in from. Auto-expanded when keys already exist, so nobody has to
          hunt for one they made earlier. */}
      <div className="rounded-lg border border-border">
        <button
          onClick={() => setShowKeys((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
          aria-expanded={showKeys}
        >
          {showKeys ? (
            <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-content-subtle" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-content-subtle" />
          )}
          <KeyIcon className="h-4 w-4 flex-shrink-0 text-content-subtle" />
          <span className="font-medium text-content">API keys</span>
          <span className="text-sm text-content-muted">
            — for cron jobs and servers with no browser
          </span>
          {keys.length > 0 && (
            <span className="ml-auto rounded-full bg-surface-sunk px-2 py-0.5 text-xs text-content-muted">
              {keys.length}
            </span>
          )}
        </button>

        {showKeys && (
          <div className="space-y-6 border-t border-border p-4">
            <p className="text-sm text-content-muted">
              A key never expires on its own unless you set it to, and it can't be shown again
              after you create it. If the thing you're connecting can open a browser, sign it in
              above instead.
            </p>
        {/* ---------------------------------------------------------------- */}
        {/* The one and only sighting of the secret                            */}
        {/* ---------------------------------------------------------------- */}
        {freshKey && (
          <div className="rounded-lg border-2 border-brand-300 bg-brand-50 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-semibold text-content">Your new key: {freshKey.name}</h4>
                <p className="mt-1 text-sm text-content-muted">
                  Copy it now. We store only a hash, so this is the last time it can be shown —
                  if you lose it you'll have to make a new one.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-stretch gap-2">
              <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-border bg-surface px-3 py-2.5 font-mono text-sm text-content">
                {freshKey.value}
              </code>
              <button
                onClick={copy}
                className={`flex flex-shrink-0 items-center gap-1.5 rounded-md px-4 text-sm font-medium transition-colors ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-brand-600 text-white hover:bg-brand-700'
                }`}
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4" /> Copy
                  </>
                )}
              </button>
            </div>

            <div className="mt-4 rounded-md bg-gray-900 px-4 py-3">
              <p className="mb-1.5 text-xs text-gray-400">Connect Claude Code:</p>
              <code className="block overflow-x-auto whitespace-pre text-xs leading-6 text-gray-300">
                {`claude mcp add --transport http clevor ${window.location.origin}/api/mcp \\\n  --header "Authorization: Bearer ${freshKey.value}"`}
              </code>
              <p className="mt-2 text-xs text-gray-500">
                For Claude Code, Cursor, VS Code, Zed and your own scripts. ChatGPT and the Claude
                apps sign in instead of using a key: add the same address as a connector and approve
                the sign-in when they ask.
              </p>
            </div>

            {/* Dismissal is deliberate and labelled, never an X in a corner. */}
            <button
              onClick={() => setFreshKey(null)}
              className="mt-4 text-sm font-medium text-content-muted underline hover:text-content"
            >
              I've copied it — hide this key
            </button>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Create                                                            */}
        {/* ---------------------------------------------------------------- */}
        {showForm ? (
          <form onSubmit={create} className="rounded-lg border border-border bg-surface-sunk p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label htmlFor="key-name" className="label">
                  What is this key for?
                </label>
                <input
                  id="key-name"
                  className="input"
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Claude on my laptop"
                  autoFocus
                />
              </div>
              <div className="sm:w-40">
                <label htmlFor="key-expiry" className="label">
                  Expires
                </label>
                <select
                  id="key-expiry"
                  className="input"
                  value={expiresInDays ?? ''}
                  onChange={(e) =>
                    setExpiresInDays(e.target.value ? Number(e.target.value) : undefined)
                  }
                >
                  {EXPIRY_CHOICES.map((c) => (
                    <option key={c.label} value={c.value ?? ''}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!name.trim() || chosen.length === 0 || creating}
                  className="btn-primary px-5 py-2.5 disabled:opacity-60"
                >
                  {creating ? 'Creating…' : 'Create key'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-md border border-border px-4 py-2.5 text-sm font-medium text-content-muted hover:text-content"
                >
                  Cancel
                </button>
              </div>
            </div>
            {/* Scopes are opt-out rather than opt-in: all on by default, because a
                key that can do nothing is a support ticket. But they are visible
                and unticking one genuinely narrows what the key can reach — the
                server refuses the tool, and MCP never even lists it.

                One exception, below: the permission to spend credits is opt-in.
                Every other permission is undone by revoking the key. Spent
                credits are not. */}
            <fieldset className="mt-4">
              <legend className="label mb-2">What this key can read and change</legend>
              <div className="space-y-2">
                {scopes.filter((sc) => sc.id !== SPEND_SCOPE).map((scope) => (
                  <label key={scope.id} className="flex items-start gap-2.5 text-sm">
                    <input
                      type="checkbox"
                      checked={chosen.includes(scope.id)}
                      onChange={(e) =>
                        setChosen((prev) =>
                          e.target.checked
                            ? [...prev, scope.id]
                            : prev.filter((id) => id !== scope.id),
                        )
                      }
                      className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                    />
                    <span>
                      <code className="text-xs text-content-muted">{scope.id}</code>
                      <span className="block text-content">{scope.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {scopes.some((sc) => sc.id === SPEND_SCOPE) && (
              <fieldset className="mt-4 rounded-lg border border-amber-300 bg-amber-50/60 p-3">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Spending your AI credits
                </legend>
                <label className="flex items-start gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={chosen.includes(SPEND_SCOPE)}
                    onChange={(e) =>
                      setChosen((prev) =>
                        e.target.checked
                          ? [...prev, SPEND_SCOPE]
                          : prev.filter((id) => id !== SPEND_SCOPE),
                      )
                    }
                    className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <code className="text-xs text-amber-800">{SPEND_SCOPE}</code>
                    <span className="block text-content">
                      Let this key run AI analysis, partner matching and market research.
                    </span>
                  </span>
                </label>

                {chosen.includes(SPEND_SCOPE) && (
                  <div className="mt-3 border-t border-amber-200 pt-3">
                    <label htmlFor="key-cap" className="label">
                      Most credits it may spend in a day
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        id="key-cap"
                        type="number"
                        min={1}
                        max={1000}
                        value={dailyCreditCap}
                        onChange={(e) => setDailyCreditCap(Number(e.target.value))}
                        className="input w-28"
                      />
                      <span className="text-sm text-content-muted">credits per day</span>
                    </div>
                    {/* The account balance says how much there is to lose. It
                        says nothing about how fast, and an agent asked to
                        "check everything I'm watching" will happily turn one
                        sentence into forty analyses. */}
                    <p className="mt-2 text-xs text-amber-900">
                      An analysis costs 1 credit. This is the ceiling for the whole day, resetting
                      at midnight UTC — it stops a run-away loop or an over-eager batch from
                      spending a month's credits in an hour.
                    </p>
                  </div>
                )}
              </fieldset>
            )}

            <p className="mt-4 text-xs text-content-subtle">
              Name it after the thing that will hold it. When you revoke a key later, the name is
              all you'll have to go on.
            </p>
          </form>
        ) : (
          <button onClick={() => setShowForm(true)} className="btn-primary px-5 py-2.5">
            Create an API key
          </button>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* List                                                              */}
        {/* ---------------------------------------------------------------- */}
        {loading ? (
          <p className="text-sm text-content-muted">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border-strong p-8 text-center">
            <KeyIcon className="mx-auto h-8 w-8 text-content-subtle" />
            <p className="mt-3 text-sm text-content-muted">
              No API keys. You only need one if the thing you're connecting can't sign in.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            {keys.map((key, i) => (
              <div
                key={key.id}
                className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4 ${
                  i > 0 ? 'border-t border-border' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-content">{key.name}</p>
                  <code className="text-xs text-content-subtle">{key.prefix}…</code>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {key.permissions.map((p) => (
                      <span
                        key={p}
                        className="rounded bg-surface-sunk px-1.5 py-0.5 font-mono text-[10px] text-content-muted"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0 text-xs text-content-muted sm:w-40">
                  <span className="text-content-subtle">Last used </span>
                  {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'never'}
                  {/* A cap nobody can see the other side of is a setting, not a
                      control. Shown only for keys that can spend at all. */}
                  {key.permissions.includes(SPEND_SCOPE) && (
                    <div
                      className={`mt-0.5 ${
                        key.dailyCreditCap != null && key.creditsSpentToday >= key.dailyCreditCap
                          ? 'font-medium text-amber-700'
                          : ''
                      }`}
                    >
                      <span className="text-content-subtle">Today </span>
                      {key.creditsSpentToday}
                      {key.dailyCreditCap != null ? ` / ${key.dailyCreditCap}` : ''} credits
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-content-muted sm:w-36">
                  <span className="text-content-subtle">Expires </span>
                  {key.expiresAt ? formatDate(key.expiresAt) : 'never'}
                </div>
                <button
                  onClick={() => revoke(key)}
                  className="flex flex-shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

          </div>
        )}
      </div>

    </div>
  );
};

export default ApiKeysPanel;
